<script>
import { mapState } from 'vuex'
import date from 'locutus/php/datetime/date'
import strtotime from 'locutus/php/datetime/strtotime'
import PageNavigation from '@components/admin/page-navigation'
import { fetchPublicTradeList, fetchPublicMarkets } from '@apis/admin/market'
import fromPairs from 'lodash/fromPairs'
import pick from 'lodash/pick'
import map from 'lodash/map'
import mapKeys from 'lodash/mapKeys'

export default {
  components: { PageNavigation },
  data() {
    return {
      loading: false,
      list: [],
      public_markets: [],
      total_count: 0,
      page: 1,
      total_page: 0,
      page_navigation: {},
      search_params: {},
      excel_headers: [
        ['seq', this.$t('public_trade_list.seq')],
        ['market_seq', this.$t('public_trade_list.market_seq')],
        ['market_title', this.$t('public_trade_list.market_title')],
        ['market_code', this.$t('public_trade_list.market_code')],
        ['is_buyer_maker', this.$t('public_trade_list.is_buyer_maker')],
        ['amount', this.$t('public_trade_list.amount')],
        ['price', this.$t('public_trade_list.price')],
        ['maker_member_seq', this.$t('public_trade_list.maker_member_seq')],
        ['maker_user_id', this.$t('public_trade_list.maker_user_id')],
        ['maker_user_name', this.$t('public_trade_list.maker_user_name')],
        [
          'maker_email_address',
          this.$t('public_trade_list.maker_email_address'),
        ],
        ['maker_account_seq', this.$t('public_trade_list.maker_account_seq')],
        [
          'maker_account_number',
          this.$t('public_trade_list.maker_account_number'),
        ],
        ['maker_order_seq', this.$t('public_trade_list.maker_order_seq')],
        ['maker_fee_amount', this.$t('public_trade_list.maker_fee_amount')],
        ['taker_member_seq', this.$t('public_trade_list.taker_member_seq')],
        ['taker_user_id', this.$t('public_trade_list.taker_user_id')],
        ['taker_user_name', this.$t('public_trade_list.taker_user_name')],
        [
          'taker_email_address',
          this.$t('public_trade_list.taker_email_address'),
        ],
        ['taker_account_seq', this.$t('public_trade_list.taker_account_seq')],
        [
          'taker_account_number',
          this.$t('public_trade_list.taker_account_number'),
        ],
        ['taker_order_seq', this.$t('public_trade_list.taker_order_seq')],
        ['taker_fee_amount', this.$t('public_trade_list.taker_fee_amount')],
        ['regdate', this.$t('public_trade_list.regdate')],
      ],
      pickerOptions: {
        shortcuts: [
          {
            text: this.$t('public_trade_list.shortcuts_today'),
            onClick(picker) {
              picker.$emit('pick', [
                new Date(strtotime('today') * 1000),
                new Date(strtotime('tomorrow') * 1000 - 1),
              ])
            },
          },
          {
            text: this.$t('public_trade_list.shortcuts_yesterday'),
            onClick(picker) {
              picker.$emit('pick', [
                new Date(strtotime('yesterday') * 1000),
                new Date(strtotime('today') * 1000 - 1),
              ])
            },
          },
          {
            text: this.$t('public_trade_list.shortcuts_thismonth'),
            onClick(picker) {
              picker.$emit('pick', [
                new Date(
                  strtotime('today', strtotime('first day of this month')) *
                    1000
                ),
                new Date(
                  strtotime('today', strtotime('first day of next month')) *
                    1000 -
                    1
                ),
              ])
            },
          },
          {
            text: this.$t('public_trade_list.shortcuts_lastmonth'),
            onClick(picker) {
              picker.$emit('pick', [
                new Date(
                  strtotime('today', strtotime('first day of last month')) *
                    1000
                ),
                new Date(
                  strtotime('today', strtotime('first day of this month')) *
                    1000 -
                    1
                ),
              ])
            },
          },
          {
            text: this.$t('public_trade_list.shortcuts_thisweek'),
            onClick(picker) {
              picker.$emit('pick', [
                new Date(strtotime('today', strtotime('this week')) * 1000),
                new Date(strtotime('today', strtotime('next week')) * 1000 - 1),
              ])
            },
          },
          {
            text: this.$t('public_trade_list.shortcuts_lastweek'),
            onClick(picker) {
              picker.$emit('pick', [
                new Date(strtotime('today', strtotime('last week')) * 1000),
                new Date(strtotime('today', strtotime('this week')) * 1000 - 1),
              ])
            },
          },
          {
            text: this.$t('public_trade_list.shortcuts_week'),
            onClick(picker) {
              picker.$emit('pick', [
                new Date(strtotime('-7 day') * 1000),
                new Date(),
              ])
            },
          },
          {
            text: this.$t('public_trade_list.shortcuts_month'),
            onClick(picker) {
              picker.$emit('pick', [
                new Date(strtotime('-30 day') * 1000),
                new Date(),
              ])
            },
          },
        ],
      },
    }
  },
  computed: {
    ...mapState('common', {
      constants: (state) => state.constants,
    }),
    c_regdate_range: {
      get() {
        const regdate_start = this.search_params.regdate_start || ''
        const regdate_end = this.search_params.regdate_end || ''
        return [
          regdate_start ? new Date(regdate_start * 1) : '',
          regdate_end ? new Date(regdate_end * 1) : '',
        ]
      },
      set([start, end]) {
        this.$set(
          this.search_params,
          'regdate_start',
          start ? `${start.getTime()}` : undefined
        )
        this.$set(
          this.search_params,
          'regdate_end',
          end ? `${end.getTime()}` : undefined
        )
      },
    },
  },
  watch: {
    $route(to, from) {
      this.page = this.$route.query.page || 1
      this.loadList().catch((e) => this.$toasted.error(e.message))
    },
  },
  created() {
    this.init()
  },
  methods: {
    init() {
      this.page = this.$route.query.page || 1
      this.search_params = { ...this.$route.query }
      Promise.all([this.loadList(), this.loadPublicMarkets()])
        .catch((e) => this.$toasted.error(e.message))
        .finally(() => {
          this.loading = false
        })
    },
    async loadList() {
      this.loading = true
      const ret_obj = await fetchPublicTradeList({
        ...this.$route.params,
        ...this.$route.query,
      })

      this.list = ret_obj.data
      this.total_count = ret_obj.total_count
      this.total_page = ret_obj.total_page
      this.page_navigation = ret_obj.page_navigation
    },
    async loadPublicMarkets() {
      const { data } = await fetchPublicMarkets()

      this.public_markets = data
    },
    search() {
      this.$router.push({ query: this.search_params })
    },
    clearAndSearch() {
      this.search_params = pick(this.search_params, 'market_seq')
      this.search()
    },
    async handleExportExcel() {
      this.loading = true
      try {
        if (
          !(await this.$confirm(
            this.$t('public_trade_list.msg_server_overload'),
            this.$t('admin.modal_confirm'),
            {
              type: 'warning',
            }
          ).catch(() => false))
        )
          return

        const { data } = await fetchPublicTradeList({
          ...this.$route.params,
          ...this.$route.query,
          no_paging: 1,
        })

        const { default: exportJsonToExcel } = await import(
          '@utils/export-json-to-excel'
        )
        exportJsonToExcel({
          header: map(this.excel_headers, ([, title]) => title),
          data: this.format2excel(data),
          filename: this.$t('public_trade_list.b2c_engagemen', [
            date('YmdHis'),
          ]),
        })
      } catch (e) {
        this.$toasted.error(e.message)
      } finally {
        this.loading = false
      }
    },
    format2excel(list) {
      const excel_fields = map(this.excel_headers, ([name]) => name)
      return map(list, (data) => {
        let result = pick(data, excel_fields)
        const keys = Object.keys(result)
        result = mapKeys(
          map(result, (val, key) => {
            switch (key) {
              case 'side_type':
              case 'status':
                return fromPairs(this.constants.market.public_order[key])[val]
              case 'regdate':
                return val ? date('Y-m-d H:i:s', new Date(val)) : ''
              default:
                return val
            }
          }),
          (val, key) => {
            return keys[key]
          }
        )

        return map(excel_fields, (key) => result[key])
      })
    },
    date,
    fromPairs,
  },
}
</script>

<template>
  <div v-loading="loading">
    <h3 :class="$style.xeAdmin">{{ $t('admin.search') }}</h3>
    <form @submit.prevent="search">
      <table cellspacing="0" cellpadding="0" :class="$style.rowTable">
        <colgroup>
          <col width="20%" />
          <col width="30%" />
          <col width="20%" />
          <col width="30%" />
        </colgroup>
        <tbody>
          <tr>
            <td colspan="4">
              <el-date-picker
                v-model="c_regdate_range"
                :class="$style.fr"
                type="datetimerange"
                :picker-options="pickerOptions"
                range-separator="To"
                start-placeholder="Start date"
                end-placeholder="End date"
                align="right"
              >
              </el-date-picker>
            </td>
          </tr>
          <tr>
            <th scope="col">
              <div>
                <select
                  v-model="search_params.search_target"
                  :class="$style.fullWidth"
                >
                  <option
                    v-for="val in [
                      [undefined, $t('admin.search_target')],
                      ['maker_user_id', $t('public_trade_list.maker_user_id')],
                      [
                        'maker_email_address',
                        $t('public_trade_list.maker_email_address'),
                      ],
                      [
                        'maker_user_name',
                        $t('public_trade_list.maker_user_name'),
                      ],
                      ['taker_user_id', $t('public_trade_list.taker_user_id')],
                      [
                        'taker_email_address',
                        $t('public_trade_list.taker_email_address'),
                      ],
                      [
                        'taker_user_name',
                        $t('public_trade_list.taker_user_name'),
                      ],
                      ['regdate_more', $t('admin.login_log_regdate_more')],
                      ['regdate_less', $t('admin.login_log_regdate_less')],
                    ]"
                    :key="val[1]"
                    :value="val[0]"
                    >{{ val[1] }}</option
                  >
                </select>
              </div>
            </th>
            <td>
              <input
                v-model="search_params.search_keyword"
                :placeholder="$t('admin.search_word')"
                type="text"
                :class="[$style.inputTypeText, $style.fullWidth]"
              />
            </td>
            <td colspan="2">
              <ElCheckbox
                v-model="search_params.maker_use_statistics_exclude"
                :false-label="undefined"
                true-label="N"
                >{{ $t('public_trade_list.except_creator') }}</ElCheckbox
              >
              <ElCheckbox
                v-model="search_params.taker_use_statistics_exclude"
                :false-label="undefined"
                true-label="N"
                >{{ $t('public_trade_list.exclude_users') }}</ElCheckbox
              >
            </td>
          </tr>
          <tr>
            <th scope="col"
              ><div>{{ $t('admin.sort') }}</div></th
            >
            <td>
              <select v-model="search_params.sort_order">
                <option
                  v-for="val in [[undefined, $t('admin.regdate')]]"
                  :key="val[1]"
                  :value="val[0]"
                  >{{ val[1] }}</option
                >
              </select>
              <select v-model="search_params.order_type">
                <option
                  v-for="val in [
                    [undefined, $t('admin.desc')],
                    ['asc', $t('admin.asc')],
                  ]"
                  :key="val[1]"
                  :value="val[0]"
                  >{{ val[1] }}</option
                >
              </select>
            </td>
          </tr>
          <tr>
            <th colspan="4" :class="$style.button">
              <a
                href="#"
                :class="[$style.button, $style.red]"
                @click.prevent="clearAndSearch"
                ><span>{{ $t('admin.reset') }}</span></a
              >
              {{ ' ' }}
              <span :class="[$style.button, $style.black]"
                ><input type="submit" :value="$t('admin.submit')"
              /></span>
            </th>
          </tr>
        </tbody>
      </table>
    </form>
    <h3 :class="$style.xeAdmin">{{ $t('public_trade_list.b2c_movements') }}</h3>
    <fieldset :class="$style.clearfix">
      <div :class="$style.fl">
        <a href="#" :class="[$style.button]" @click.prevent="handleExportExcel"
          ><span>{{ $t('admin.excel_download') }}</span></a
        >
      </div>
      <div :class="[$style.fr]" style="font-size: 18px;">
        <span :class="[$style.colorArea, $style.buy]"></span> =
        {{ $t('public_trade_list.buying') }}
        <span :class="[$style.colorArea, $style.sell]"></span> =
        {{ $t('public_trade_list.sell') }}
      </div>
    </fieldset>
    <ul :class="$style.localNavigation">
      <router-link
        v-for="{ title, seq } in [
          { title: $t('admin.all_show'), seq: undefined },
          ...public_markets,
        ]"
        :key="seq"
        :class="{ [$style.on]: seq == search_params.market_seq }"
        :to="{ query: { ...$route.query, market_seq: seq } }"
        tag="li"
      >
        <a href="#" @click.prevent>{{ title }}</a>
      </router-link>
    </ul>
    <table cellspacing="0" cellpadding="0" :class="$style.colTable">
      <caption v-if="!loading"
        >Total {{ total_count }}, Page {{ page }}/{{ total_page }}</caption
      >
      <thead>
        <tr :class="[$style.topTh]">
          <th colspan="5" scope="col"
            ><div>{{ $t('public_trade_list.common') }}</div></th
          >
          <th scope="col" colspan="3"
            ><div>{{ $t('public_trade_list.market_buildera') }}</div></th
          >
          <th scope="col" colspan="3"
            ><div>{{ $t('public_trade_list.market_user') }}</div></th
          >
        </tr>
        <tr>
          <th scope="col" rowspan="2"
            ><div
              ><a href="#">{{ $t('admin.num') }}</a></div
            ></th
          >
          <th scope="col" rowspan="2"
            ><div>{{ $t('admin.market_title') }}</div></th
          >
          <th scope="col" rowspan="2"
            ><div>{{ $t('public_trade_list.unique_number') }}</div></th
          >
          <th scope="col"
            ><div>{{ $t('admin.regdate') }}</div></th
          >
          <th scope="col"
            ><div>{{ $t('public_trade_list.amount') }}</div></th
          >
          <th scope="col" :class="$style.quarter_wide"
            ><div>{{ $t('public_trade_list.id') }}</div></th
          >
          <th scope="col"
            ><div>{{ $t('public_trade_list.account_number') }}</div></th
          >
          <th scope="col" rowspan="2"
            ><div>{{ $t('public_trade_list.fee') }}</div></th
          >
          <th scope="col" :class="$style.quarter_wide"
            ><div>{{ $t('public_trade_list.id') }}</div></th
          >
          <th scope="col"
            ><div>{{ $t('public_trade_list.account_number') }}</div></th
          >
          <th scope="col" rowspan="2"
            ><div>{{ $t('public_trade_list.fee') }}</div></th
          >
        </tr>
        <tr>
          <th scope="col"
            ><div>{{
              $t('public_trade_list.quantity_constructor_status')
            }}</div></th
          >
          <th scope="col"
            ><div>{{ $t('public_trade_list.price') }}</div></th
          >
          <th scope="col" :class="$style.quarter_wide"
            ><div>{{ $t('public_trade_list.name') }}</div></th
          >
          <th scope="col"
            ><div>{{ $t('public_trade_list.order_unique_number') }}</div></th
          >
          <th scope="col" :class="$style.quarter_wide"
            ><div>{{ $t('public_trade_list.name') }}</div></th
          >
          <th scope="col"
            ><div>{{ $t('public_trade_list.order_unique_number') }}</div></th
          >
        </tr>
      </thead>
      <tbody>
        <template v-for="(data, idx) in list">
          <tr :key="`1-${data.seq}`" :class="$style[`bg${idx % 2}`]">
            <td rowspan="2" :class="[$style.tCenter, $style.nowrap]">{{
              data._no
            }}</td>
            <td rowspan="2" :class="[$style.wide]">
              {{ data.market_title }}
            </td>
            <td rowspan="2" :class="[$style.tCenter, $style.nowrap]">{{
              data.seq
            }}</td>
            <td :class="[$style.tCenter, $style.nowrap]">{{
              date('Y-m-d H:i:s', new Date(data.regdate))
            }}</td>
            <td :class="[$style.tRight, $style.nowrap]"
              >{{ data.amount | trimDecimalZero | numberFormat }}
              {{ data.source_asset_code }}
            </td>
            <td
              :class="[
                $style[data.is_buyer_maker],
                $style.maker,
                $style.tCenter,
                $style.nowrap,
              ]"
              >{{ data.maker_user_id }}</td
            >
            <td
              :class="[
                $style[data.is_buyer_maker],
                $style.maker,
                $style.tCenter,
                $style.nowrap,
              ]"
              >{{ data.maker_account_number }}</td
            >
            <td
              rowspan="2"
              :class="[
                $style[data.is_buyer_maker],
                $style.maker,
                $style.tRight,
                $style.nowrap,
              ]"
              >{{ data.maker_fee_amount | trimDecimalZero | numberFormat }}
              {{
                data.is_buyer_maker === 'Y'
                  ? data.source_asset_code
                  : data.target_asset_code
              }}
            </td>
            <td
              :class="[
                $style[data.is_buyer_maker],
                $style.taker,
                $style.tCenter,
                $style.nowrap,
              ]"
              >{{ data.taker_user_id }}</td
            >
            <td
              :class="[
                $style[data.is_buyer_maker],
                $style.taker,
                $style.tCenter,
                $style.nowrap,
              ]"
              >{{ data.taker_account_number }}</td
            >
            <td
              rowspan="2"
              :class="[
                $style[data.is_buyer_maker],
                $style.taker,
                $style.tRight,
                $style.nowrap,
              ]"
              >{{ data.taker_fee_amount | trimDecimalZero | numberFormat }}
              {{
                data.is_buyer_maker === 'Y'
                  ? data.target_asset_code
                  : data.source_asset_code
              }}
            </td>
          </tr>
          <tr :key="`2-${data.seq}`" :class="$style[`bg${idx % 2}`]">
            <td :class="[$style.tCenter, $style.nowrap]"
              >{{ data.is_buyer_maker }}
            </td>
            <td :class="[$style.tRight, $style.nowrap]"
              >{{ data.price | trimDecimalZero | numberFormat }}
              {{ data.target_asset_code }}
            </td>
            <td
              :class="[
                $style[data.is_buyer_maker],
                $style.maker,
                $style.tCenter,
                $style.nowrap,
              ]"
              >{{ data.maker_user_name }}</td
            >
            <td
              :class="[
                $style[data.is_buyer_maker],
                $style.maker,
                $style.tCenter,
                $style.nowrap,
              ]"
              >{{ data.maker_order_seq }}</td
            >
            <td
              :class="[
                $style[data.is_buyer_maker],
                $style.taker,
                $style.tCenter,
                $style.nowrap,
              ]"
              >{{ data.taker_user_name }}</td
            >
            <td
              :class="[
                $style[data.is_buyer_maker],
                $style.taker,
                $style.tCenter,
                $style.nowrap,
              ]"
              >{{ data.taker_order_seq }}</td
            >
          </tr>
        </template>
        <tr v-if="loading">
          <td :colspan="11" :class="$style.tCenter">{{
            $t('admin.loading')
          }}</td>
        </tr>
        <tr v-else-if="!list.length">
          <td :colspan="11" :class="$style.tCenter">{{
            $t('public_trade_list.msg_no_b2c')
          }}</td>
        </tr>
      </tbody>
    </table>
    <PageNavigation
      v-if="!loading"
      :total_count="total_count"
      :total_page="page_navigation.total_page"
      :cur_page="page"
      :page_count="page_navigation.page_count"
    ></PageNavigation>
  </div>
</template>

<style lang="scss" module>
@import '@src/design/default.scss';
@import '@src/design/admin.scss';
@import '@src/design/font.scss';
@import '@src/design/button.scss';

.colTable .topTh th {
  color: #fff;
  background-color: #1e2f3c;
}
.colTable th,
.colTable td {
  vertical-align: middle;
}

.colorArea {
  display: inline-block;
  width: 30px;
  height: 15px;
}

.buy,
.colTable td.maker.Y,
.colTable td.taker.N {
  color: #fff;
  background-color: #d60000;
}

.sell,
.colTable td.maker.N,
.colTable td.taker.Y {
  color: #fff;
  background-color: #0051c7;
}
</style>
